import React, { useEffect, useState } from "react";
import { Edit, Trash2, ChevronDown, ChevronUp, User } from "lucide-react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import axiosInstance from "../../utils/axios";
import uploadImage from "../../utils/CloudinaryUpload";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

const AdminBlogs = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    images: [],
    tags: "",
    author: "",
    publishedDate: "",
    status: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const postsPerPage = 6;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/blogs/all-blogs");
        // console.log("blogs", data);
        setPosts(data);
      } catch {
        setError("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = (post) => {
    setBlogToDelete(post);
    setIsDeleteModal(true);
  };
  const handleConfirmDelete = async () => {
    if (!blogToDelete) return;
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/blogs/delete-blog/${blogToDelete._id}`);
      setPosts((prev) => prev.filter((p) => p._id !== blogToDelete._id));
      toast.success("Blog deleted successfully!");
    } catch {
      toast.error("Failed to delete blog.");
    } finally {
      setDeleteLoading(false);
      setIsDeleteModal(false);
      setBlogToDelete(null);
    }
  };

  const handleCreate = () => {
    setEditingPostId(null);
    setNewPost({
      title: "",
      content: "",
      images: [],
      tags: "",
      author: "",
      publishedDate: "",
      status: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (postId) => {
    const post = posts.find((p) => p._id === postId);
    if (!post) return;

    setEditingPostId(postId);
    setNewPost({
      title: post.title || "",
      content: post.content || "",
      images: post.images || [],
      tags: (post.tags || []).join(", "),
      author: post.author || "",
      publishedDate: post.publishedDate?.slice(0, 10) || "",
      status: post.status || "",
    });
    setIsModalOpen(true);
  };

  const handleSearch = (term) => {
    // console.log("serarch term:",term)
    setSearchTerm(term.trim());
  };

  const handleModalSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        title: newPost.title,
        content: newPost.content,
        images: newPost.images,
        tags: newPost.tags.split(",").map((tag) => tag.trim()),
        author: newPost.author,
        publishedDate: newPost.publishedDate,
        status: newPost.status,
      };

      let response;
      if (editingPostId) {
        response = await axiosInstance.put(
          `/blogs/edit-blog/${editingPostId}`,
          payload
        );
        setPosts((prev) =>
          prev.map((post) =>
            post._id === editingPostId ? response.data.blog : post
          )
        );
        toast.success("Post updated successfully!");
      } else {
        response = await axiosInstance.post("/blogs/add-blog", payload);
        setPosts((prev) => [response.data.blog, ...prev]);
        setCurrentPage(1);
        toast.success("Post created successfully!");
      }

      setIsModalOpen(false);
      setEditingPostId(null);
      setNewPost({
        title: "",
        content: "",
        images: [],
        tags: "",
        author: "",
        publishedDate: "",
        status: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to save post.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredPosts = posts.filter(
    (post) =>
      post?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post?.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const start = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(start, start + postsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <AdminSearchBar
        title="All Blogs"
        onAddNewClick={handleCreate}
        onSearchClick={handleSearch}
      />
      <div className="container mx-auto px-4 py-6">
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="animate-pulse p-4 rounded-lg shadow-md bg-white"
              >
                <div className="h-48 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-40 mb-4"></div>
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-1"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="flex gap-6 mt-4">
                  <div className="h-4 w-10 bg-gray-300 rounded"></div>
                  <div className="h-4 w-10 bg-gray-300 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && filteredPosts.length === 0 && (
          <p className="text-center text-slate-500 text-lg mt-8">
            No blogs available.
          </p>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          {currentPosts.map((post) => (
            <div
              key={post._id}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200 hover:shadow-lg transition-all duration-300"
            >
              <div
                onClick={() => {
                  navigate(`/admin-blog-details/${post._id}`);
                }}
                className="relative"
              >
                <img
                  src={post?.images?.[0]}
                  alt={post.title}
                  className="w-full h-60 object-cover cursor-pointer"
                />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex w-full items-center gap-2 mb-2">
                      {/* Status badge */}
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full 
      ${
        post.status === "draft"
          ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
          : post.status === "published"
          ? "bg-green-100 text-green-700 border border-green-300"
          : "bg-gray-100 text-gray-600 border border-gray-300"
      }`}
                      >
                        {post.status}
                      </span>

                      {/* Date badge */}
                      <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                        {post.publishedDate?.slice(0, 10) || "No date"}
                      </span>

                      {/* Author */}
                      <span className="text-xs text-slate-500 flex items-center">
                        <User size={10} className="mr-1" />
                        {post.author || "Admin"}
                      </span>
                    </div>

                    <h2
                      onClick={() => {
                        navigate(`/admin-blog-details/${post._id}`);
                      }}
                      className="text-lg line-clamp-2 h-14 cursor-pointer font-bold text-slate-800 mb-2 hover:text-blue-600 transition-colors"
                    >
                      {post.title}
                    </h2>
                  </div>
                </div>
                <div
                  className={`text-slate-600 text-sm transition-all h-12 overflow-hidden`}
                >
                  <p className={"line-clamp-2"}>{post.content}</p>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(post._id)}
                      className="flex items-center text-blue-600 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50 text-sm"
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(post)}
                      className="flex items-center text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50 text-sm"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-lg text-sm ${
                currentPage === 1
                  ? "text-slate-400"
                  : "text-slate-600 hover:text-blue-600 hover:bg-blue-50"
              }`}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-2 rounded-lg text-sm ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-lg text-sm ${
                currentPage === totalPages
                  ? "text-slate-400"
                  : "text-slate-600 hover:text-blue-600 hover:bg-blue-50"
              }`}
            >
              Next
            </button>
          </div>
        )}

        {isModalOpen &&
          createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className="bg-white w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl px-6 pb-6 rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto">
                {/* Sticky Header */}
                <div className="sticky top-0 bg-white z-20 py-4 border-b flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-slate-800">
                    {editingPostId ? "Edit Post" : "Create New Blog"}
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingPostId(null);
                    }}
                    aria-label="Close modal"
                    className="text-gray-600 hover:text-gray-800 transition p-2 rounded-full hover:bg-slate-100"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Form Fields */}
                <div className="mt-6 space-y-4">
                  <input
                    type="text"
                    placeholder="Title"
                    value={newPost.title}
                    onChange={(e) =>
                      setNewPost({ ...newPost, title: e.target.value })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  <textarea
                    rows={4}
                    placeholder="Content"
                    value={newPost.content}
                    onChange={(e) =>
                      setNewPost({ ...newPost, content: e.target.value })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  {/* Image Upload */}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (!files.length) return;

                      const uploadedUrls = [];
                      for (const file of files) {
                        try {
                          const url = await uploadImage(file);
                          uploadedUrls.push(url);
                        } catch (err) {
                          console.error("Upload failed", err);
                          alert("Image upload failed.");
                        }
                      }
                      setNewPost((prev) => ({
                        ...prev,
                        images: [...prev.images, ...uploadedUrls],
                      }));
                    }}
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  {newPost.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {newPost.images.map((url, i) => (
                        <div
                          key={i}
                          className="relative group h-20 w-full max-w-32 rounded overflow-hidden shadow"
                        >
                          <img
                            src={url}
                            alt={`preview-${i}`}
                            className="object-cover w-full h-full"
                          />
                          <button
                            onClick={() =>
                              setNewPost((prev) => ({
                                ...prev,
                                images: prev.images.filter(
                                  (_, index) => index !== i
                                ),
                              }))
                            }
                            className="absolute top-1 right-1 bg-white rounded-full  w-6 h-6 text-red-500 hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Tags (comma-separated)"
                    value={newPost.tags}
                    onChange={(e) =>
                      setNewPost({ ...newPost, tags: e.target.value })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  <input
                    type="text"
                    placeholder="Author"
                    value={newPost.author}
                    onChange={(e) =>
                      setNewPost({ ...newPost, author: e.target.value })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  <input
                    type="date"
                    value={newPost.publishedDate}
                    onChange={(e) =>
                      setNewPost({
                        ...newPost,
                        publishedDate: e.target.value,
                      })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  />

                  <select
                    value={newPost.status}
                    onChange={(e) =>
                      setNewPost({ ...newPost, status: e.target.value })
                    }
                    className="w-full border px-4 py-2 rounded-lg focus:ring focus:ring-blue-200"
                  >
                    <option value="">Select Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 mt-8 border-t pt-4">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingPostId(null);
                    }}
                    className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleModalSubmit}
                    disabled={isSubmitting}
                    className={`px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center min-w-[120px] ${
                      isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmitting
                      ? "Loading..."
                      : editingPostId
                      ? "Save Changes"
                      : "Create"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
      {isDeleteModal && blogToDelete && (
        <DeleteBlogModal
          loading={deleteLoading}
          onClose={() => {
            setIsDeleteModal(false);
            setBlogToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};
export const DeleteBlogModal = ({ loading, onClose, onConfirm }) => {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">
          Confirm Delete
        </h2>
        <p className="text-slate-600 mb-6">
          Are you sure you want to delete this blog ? This action cannot be
          undone.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdminBlogs;
